import {
  Form,
  FormField,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  DateTimePicker,
} from "@/components/ui";
import { useAppDispatch, useAppSelector } from "@/config/store";
import {
  IEdoCourseEvent,
  defaultValue,
} from "@/shared/model/edo-course-event.model";
import { IEdoCourse } from "@/shared/model/edo-course.model";
import {
  createEntity,
  updateEntity,
} from "@/shared/reducers/entities/edo-course-event.reducer";
import { EntityState } from "@/shared/reducers/reducer.utils";
import { convertDateTimeToServer } from "@/shared/util/date-utils";
import { useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Translate, translate } from "react-jhipster";
import { z } from "zod";

const schema = z.object({
  title: z.string().min(1, "Required"),
  description: z.string().min(1, "Required"),
  startDate: z.string().nullable().optional(),
  endDate: z.string().nullable().optional(),
});

type FormValues = z.infer<typeof schema>;

const EditCourseEventFormContent = () => {
  const { control, watch } = useFormContext<FormValues>();
  const startDate = watch("startDate");
  const minEnd = startDate
    ? new Date(startDate).toISOString().slice(0, 16)
    : undefined;

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <div className="col-span-full">
        <FormField
          name="title"
          label={translate("edocendoApp.edoCourseEvent.title")}
          required
        />
      </div>
      <div className="col-span-full">
        <FormField
          name="description"
          label={translate("edocendoApp.edoCourseEvent.description")}
          required
        />
      </div>
      <div className="col-span-full sm:col-span-1">
        <Controller
          name="startDate"
          control={control}
          render={({ field }) => (
            <DateTimePicker
              label={translate("edocendoApp.edoCourseEvent.startDate")}
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
      </div>
      <div className="col-span-full sm:col-span-1">
        <Controller
          name="endDate"
          control={control}
          render={({ field }) => (
            <DateTimePicker
              label={translate("edocendoApp.edoCourseEvent.endDate")}
              value={field.value}
              onChange={field.onChange}
              min={minEnd}
            />
          )}
        />
      </div>
    </div>
  );
};

interface IEditCourseEventProps {
  open: boolean;
  courseEvent?: IEdoCourseEvent;
  course?: IEdoCourse;
  onClose: () => void;
}

const EditCourseEvent = (props: IEditCourseEventProps) => {
  const dispatch = useAppDispatch();
  const [form, setForm] = useState<IEdoCourseEvent>({
    ...defaultValue,
    ...props.courseEvent,
  });
  const { loading, updating, updateSuccess } = useAppSelector<
    EntityState<IEdoCourseEvent>
  >((state) => state.edoCourseEvent);

  const isNew =
    props.courseEvent === null || props.courseEvent === undefined;
  const { open, courseEvent, course, onClose: handleClose } = props;

  useEffect(() => {
    setForm({ ...defaultValue, ...courseEvent });
  }, [courseEvent]);

  useEffect(() => {
    if (updateSuccess) handleClose();
  }, [updateSuccess]);

  const saveEntity = (data: FormValues) => {
    const payload = {
      ...form,
      title: data.title,
      description: data.description,
      startDate: convertDateTimeToServer(
        data.startDate || (form.startDate as string)
      ),
      endDate: convertDateTimeToServer(
        data.endDate || (form.endDate as string)
      ),
    };
    if (!courseEvent || !courseEvent.id) {
      dispatch(createEntity({ ...payload, course } as any));
    } else {
      dispatch(updateEntity(payload as any));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {isNew
              ? translate("edocendoApp.edoCourseEvent.home.createLabel")
              : translate("edocendoApp.edoCourseEvent.home.editLabel")}
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center py-4">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        ) : (
          <>
            <p className="mb-4 text-body text-[var(--color-mockup-text-mid)]">
              Aggiungi le informazioni dell'evento.
            </p>
            <Form<FormValues>
              id="edit-course-event-form"
              key={`${form.id ?? "new"}`}
              schema={schema}
              onSubmit={saveEntity}
              defaultValues={{
                title: form.title ?? "",
                description: form.description ?? "",
                startDate: form.startDate ?? null,
                endDate: form.endDate ?? null,
              }}
              className="flex flex-col gap-4"
            >
              <EditCourseEventFormContent />
            </Form>
          </>
        )}
        <DialogFooter>
          <Button variant="outline-solid" onClick={handleClose}>
            <Translate contentKey="entity.action.cancel">Cancel</Translate>
          </Button>
          <Button
            type="submit"
            form="edit-course-event-form"
            isLoading={updating}
          >
            {isNew
              ? translate("entity.action.add")
              : translate("entity.action.edit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditCourseEvent;
